import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useMemo } from "react";
import ProductDataTable from "../../SalesPeronsWithChallanData/components/ProductDataTable";
import {
    ProductDetailsIcon,
    TopProductIcon,
    TotalProductsIcon,
    TotalQtyIcon,
} from "./SvgIcons";
import TypographyCompo from "../../../components/Comman/TypographyCompo";
import ButtonComponent from "../../../components/Comman/ButtonComponent";
import {
    grayShadeLightColor,
    greyStrongColor,
    lightWhiteRedShade,
    strongBlue,
    white,
} from "../../../config/ColorObj";

// ─── Styled Dialog ────────────────────────────────────────────────────────────
const BootstrapDialog = styled(Dialog)(() => ({
    "& .MuiDialog-paper": {
        borderRadius: "12px",
        overflow: "hidden",
    },
}));

// ─── Dialog Header ────────────────────────────────────────────────────────────
const DialogHeader = ({ onClose }) => (
    <Box sx={styles.header}>
        <Box sx={styles.headerLeft}>
            <ProductDetailsIcon />
            <TypographyCompo style={styles.headerTitle}>
                Product Details
            </TypographyCompo>
        </Box>
        <ButtonComponent
            onClick={onClose}
            style={styles.closeBtn}
            aria-label="Close"
        >
            <CloseIcon sx={{ fontSize: 16 }} />
        </ButtonComponent>
    </Box>
);

DialogHeader.propTypes = {
    onClose: PropTypes.func.isRequired,
};

// ─── Summary Strip ────────────────────────────────────────────────────────────
const SummaryStrip = ({ metrics }) => (
    <Box sx={styles.summaryStrip}>
        {metrics.map(({ label, value, icon: Icon }) => (
            <Box key={label} sx={styles.metricCard}>
                <Box sx={styles.metricLabelRow}>
                    {Icon && <Icon />}
                    <TypographyCompo style={styles.metricLabel}>{label}</TypographyCompo>
                </Box>
                <TypographyCompo style={styles.metricValue}>{value}</TypographyCompo>
            </Box>
        ))}
    </Box>
);

SummaryStrip.propTypes = {
    metrics: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            icon: PropTypes.elementType,
        }),
    ).isRequired,
};

// ─── Footer ───────────────────────────────────────────────────────────────────
const DialogFooter = ({ onClose }) => (
    <Box sx={styles.footer}>
        <ButtonComponent onClick={onClose} style={styles.primaryBtn}>
            Close
        </ButtonComponent>
    </Box>
);

DialogFooter.propTypes = {
    onClose: PropTypes.func.isRequired,
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ProductDataPopUp = ({ handleCloses, open, rowData = [] }) => {
    const productSellData = useMemo(
        () =>
            rowData.map((item) => ({
                productName: item.name,
                sellQty: item.billQty,
            })),
        [rowData],
    );

    const productMetrics = useMemo(() => {
        const total = productSellData.reduce((s, r) => s + (r.sellQty ?? 0), 0);
        const top = productSellData.reduce(
            (a, b) => ((b.sellQty ?? 0) > (a.sellQty ?? 0) ? b : a),
            productSellData[0],
        );
        return [
            {
                label: "Total Products",
                value: productSellData.length,
                icon: TotalProductsIcon,
            },
            { label: "Total Qty Sold", value: total, icon: TotalQtyIcon },
            {
                label: "Top Product",
                value: top?.productName ?? "—",
                icon: TopProductIcon,
            },
        ];
    }, [productSellData]);

    return (
        <BootstrapDialog open={open} fullWidth maxWidth="md" onClose={handleCloses}>
            <DialogHeader onClose={handleCloses} />

            <DialogContent sx={styles.dialogContent}>
                <SummaryStrip metrics={productMetrics} />
                <ProductDataTable rows={productSellData} />
            </DialogContent>

            <DialogFooter onClose={handleCloses} />
        </BootstrapDialog>
    );
};

ProductDataPopUp.propTypes = {
    handleCloses: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    rowData: PropTypes.array,
};

export default ProductDataPopUp;

// ─── Styles (unchanged) ───────────────────────────────────────────────────────
const styles = {
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderBottom: "0.5px solid",
        borderColor: "divider",
    },
    headerLeft: { display: "flex", alignItems: "center", gap: 1.25 },
    headerTitle: { fontSize: 16, fontWeight: 600, color: "inherit" },
    closeBtn: {
        width: 28,
        height: 28,
        border: "0.5px solid #ccc",
        borderRadius: 6,
        background: "transparent",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: grayShadeLightColor,
    },
    dialogContent: { p: 3, backgroundColor: lightWhiteRedShade },
    summaryStrip: { display: "flex", gap: 1.5, mb: 2.5 },
    metricCard: {
        flex: 1,
        backgroundColor: "background.paper",
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: "10px 14px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
    },
    metricLabelRow: { display: "flex", alignItems: "center", gap: "5px" },
    metricLabel: {
        fontSize: 12,
        color: greyStrongColor,
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
    },
    metricValue: { fontSize: 18, fontWeight: 600, color: "inherit" },
    footer: {
        display: "flex",
        justifyContent: "flex-end",
        gap: 1,
        px: 3,
        py: 1.75,
        borderTop: "0.5px solid",
        borderColor: "divider",
    },
    primaryBtn: {
        fontSize: 13,
        padding: "7px 18px",
        border: "none",
        borderRadius: 6,
        background: strongBlue,
        cursor: "pointer",
        color: white,
        fontWeight: 600,
    },
};
